import React, { FC, memo, useEffect, useMemo, useState } from 'react';

import { DropDownOptions } from 'devextreme-react/lookup';

import { AppFieldBox } from 'components/appComponents';
import { useAuth } from 'context/auth';
import { SelectBox } from 'devextreme-react';
import DataSource from 'devextreme/data/data_source';
import { ContractorLegalEditAddModal } from 'pages/Contractor';

type ContractorLegalListItem = {
  id: number;
  name: string;
  inn: string;
};

type ContractorLegalSelectProps = {
  label: string;
  placeholder: string;
  value: number | null;
  readOnly: boolean;
  storeContractorLegal: DataSource;
  onChange: (value: number | null) => void;
  /** Кнопка «+» и модалка создания юр. лица (по умолчанию по праву CanCreateLegalContractor) */
  showCreateButton?: boolean;
  /**
   * Вызывать `dataSource.load()` при монтировании (несколько селектов на одном store — один вызов).
   * `false`: список подгрузится при открытии выпадающего списка — меньше запросов (напр. модалка ТТН 2).
   */
  preloadOnMount?: boolean;
};

/** Исключает два параллельных preload одного экземпляра DataSource (две строки формы на одном store). */
const contractorLegalPreloadScheduled = new WeakSet<DataSource>();

const ContractorLegalSelect: FC<ContractorLegalSelectProps> = ({
  label,
  placeholder,
  value,
  readOnly,
  storeContractorLegal,
  onChange,
  showCreateButton = true,
  preloadOnMount = true,
}) => {
  const { getClaims }: any = useAuth();

  const [visibleLegalModal, setVisibleLegalModal] = useState<boolean>(false);

  useEffect(() => {
    if (!preloadOnMount) return;
    const ds = storeContractorLegal;
    if (contractorLegalPreloadScheduled.has(ds)) return;
    contractorLegalPreloadScheduled.add(ds);
    void ds.load();
  }, [preloadOnMount, storeContractorLegal]);

  const canCreateLegal = showCreateButton && getClaims(['CanCreateLegalContractor']);

  const refresh = (): void => {
    storeContractorLegal.load();
  };

  const searchExpr = useMemo(() => ['name', 'inn'] as const, []);

  return (
    <>
      {visibleLegalModal ? (
        <ContractorLegalEditAddModal
          visible={visibleLegalModal}
          changeVisible={(show) => setVisibleLegalModal(show)}
          id={null}
          refresh={refresh}
        />
      ) : null}

      <AppFieldBox label={label} labelTop>
        <div className="d-flex align-center">
          <SelectBox
            stylingMode="underlined"
            readOnly={readOnly}
            style={{ width: '100%' }}
            dataSource={storeContractorLegal}
            placeholder={placeholder}
            displayExpr="name"
            valueExpr="id"
            searchEnabled={true}
            searchExpr={[...searchExpr]}
            value={value}
            onItemClick={(e: { itemData?: ContractorLegalListItem }) => {
              if (e.itemData) {
                onChange(e.itemData.id);
              }
            }}
            buttons={[
              'dropDown',
              {
                name: 'clearLegal',
                location: 'after' as const,
                options: {
                  onClick: () => onChange(null),
                  icon: 'close',
                  disabled: readOnly,
                },
              },
              ...(canCreateLegal
                ? [
                    {
                      name: 'addLegal',
                      location: 'after' as const,
                      options: {
                        onClick: () => setVisibleLegalModal(true),
                        icon: 'plus',
                        disabled: readOnly,
                      },
                    },
                  ]
                : []),
            ]}
          >
            <DropDownOptions title={placeholder} closeOnOutsideClick showTitle={false} />
          </SelectBox>
        </div>
      </AppFieldBox>
    </>
  );
};

export default memo(ContractorLegalSelect, (prevProps, nextProps) => {
  return (
    prevProps.value === nextProps.value &&
    prevProps.readOnly === nextProps.readOnly &&
    prevProps.storeContractorLegal === nextProps.storeContractorLegal &&
    prevProps.onChange === nextProps.onChange &&
    prevProps.label === nextProps.label &&
    prevProps.placeholder === nextProps.placeholder &&
    prevProps.showCreateButton === nextProps.showCreateButton &&
    prevProps.preloadOnMount === nextProps.preloadOnMount
  );
});
